import React from 'react'
import { TouchableOpacity, View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Text } from '@src/components/base'
import { HeroChartType } from '@src/screens/Settings/models/settings.types'
import { preferredHeroChartSelector } from '@src/selectors/settings'
import { ChartSelectionCard, ChartSelectionCardProps } from './ChartSelectionCard'

const getChartCards = (
  selected: HeroChartType,
  onPress: (chartType: HeroChartType) => void,
): ChartSelectionCardProps[] => [
  {
    title: 'Glucose Chart',
    description: 'Keep track of your glucose levels and trends.',
    iconName: 'chart-line',
    onPress: () => {
      onPress(HeroChartType.GlucoseChart)
    },
    variant: 'glucose',
    selected: selected === HeroChartType.GlucoseChart,
  },
  {
    title: 'Latest Glucose Reading',
    description: 'View your most recent glucose value for a quick snapshot.',
    iconName: 'gauge',
    onPress: () => {
      onPress(HeroChartType.DialChart)
    },
    variant: 'dial',
    selected: selected === HeroChartType.DialChart,
  },
  {
    title: 'Nutrition Summary',
    description: 'Track your daily intake of calories, protein, carbs, and fat.',
    iconName: 'fork-knife',
    onPress: () => {
      onPress(HeroChartType.NutritionSummaryChart)
    },
    variant: 'nutrition',
    selected: selected === HeroChartType.NutritionSummaryChart,
  },
  {
    title: 'None',
    description: 'No chart selected',
    iconName: 'selection-slash',
    onPress: () => {
      onPress(HeroChartType.NoChart)
    },
    variant: 'none',
    selected: selected === HeroChartType.NoChart,
  },
]

export const ChooseMainChartSection = () => {
  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation()
  const dispatch = useDispatch()
  const preferredHeroChart = useSelector(preferredHeroChartSelector)

  const handleCardPress = (chartType: HeroChartType) => {
    dispatch({
      type: 'settings/setPreferredHeroChart',
      payload: { chartType },
    })
  }

  const chartCards = getChartCards(preferredHeroChart, handleCardPress)

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text type="title-3">Main Chart</Text>
        <TouchableOpacity
          accessibilityLabel="Customize Glucose Ranges"
          onPress={() => navigation.navigate('GlucoseThresholdSettings', { source: 'Home' })}
          style={styles.customizeActionText}
        >
          <Text type="regular" bold lineSpacing="none" style={styles.customizeActionText}>
            Customize Glucose Ranges
          </Text>
        </TouchableOpacity>
      </View>
      {chartCards.map((card) => (
        <View key={card.title} style={styles.cardContainer}>
          <ChartSelectionCard {...card} />
        </View>
      ))}
    </View>
  )
}

const themedStyle = StyleService.create({
  container: {
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  customizeActionText: {
    color: 'theme.text.link',
  },
  cardContainer: {
    marginBottom: 12,
  },
})
